// Deliveries in people's words, pure (deliveryModel.selftest.ts): the
// editor's form state for one delivery, the starting points it offers, the
// checks the server makes again, and how a delivery's state reads on the
// run page.
//
// A delivery is an instruction in the operator's own words with its
// settings, carried out by an agent at the run's end. The STARTING POINTS
// ("Email the results", "Upload to a Drive folder", …) only fill in an
// example instruction and the settings such a delivery usually needs;
// every field stays free text and anything else can be written. Setting
// names follow the shapes the run-time gates read (docs/workflows-
// deliveries.md §11.1): an address, a Drive folder link, a database's
// Host / Port / Database / User, a web address.

import type { StatusTone } from "@/components/ui/StatusBadge";
import type { DeliveryIn, DeliveryState, DeliveryView, DeliveryWhen } from "@/api/workflowDeliveries";

// The server's limits (schemas/workflow_deliveries.py).
export const MAX_DELIVERIES = 8;
export const MAX_INSTRUCTION = 4000;
export const MAX_SETTINGS = 20;
export const MAX_SETTING_VALUE = 2000;
export const MAX_SECRETS = 10;
export const MAX_SECRET_VALUE = 8000;
export const DEFAULT_MINUTES = 30;
export const MIN_MINUTES = 5;
export const MAX_MINUTES = 240;
const DELIVERY_ID = /^[a-z][a-z0-9-]{0,39}$/;
const SETTING_NAME = /^[A-Za-z][A-Za-z0-9 _.()/-]{0,59}$/;
const SECRET_NAME = /^[A-Za-z][A-Za-z0-9_-]{0,39}$/;

export interface SettingRow {
  key: number;
  name: string;
  value: string;
}

export interface SecretRow {
  key: number;
  name: string;
  agentMaySee: boolean;
  /** The server holds a value for it (saved before this edit). */
  hasValue: boolean;
  /** A value typed now: sent once after the save, then forgotten. "" = leave it as it is. */
  value: string;
  /** The operator asked for the stored value to go. */
  clear: boolean;
}

/** One delivery as the form edits it. */
export interface DeliveryDraft {
  id: string;
  isNew: boolean;
  instruction: string;
  settings: SettingRow[];
  secrets: SecretRow[];
  /** "" = the workflow's usual agent. */
  who: string;
  when: DeliveryWhen;
  minutes: number;
  /** The agent that drafted it ("" = the operator). */
  draftedBy: string;
}

let seq = 0;
const nextKey = () => ++seq;

export function settingRow(name = "", value = ""): SettingRow {
  return { key: nextKey(), name, value };
}

export function secretRow(name = ""): SecretRow {
  return { key: nextKey(), name, agentMaySee: false, hasValue: false, value: "", clear: false };
}

// ─── starting points ─────────────────────────────────────────────────────

export interface Starter {
  key: string;
  label: string;
  /** One line under the label. */
  hint: string;
  /** The id a new delivery from it takes (made unique). */
  id: string;
  instruction: string;
  settings: [string, string][];
  secrets: string[];
}

export const STARTERS: readonly Starter[] = [
  {
    key: "email",
    label: "Email the results",
    hint: "To the addresses you give",
    id: "email",
    instruction:
      "Email the executive summary to the addresses under To, with the report attached. Open with two or three plain sentences on what the run found.",
    settings: [["To", ""]],
    secrets: [],
  },
  {
    key: "drive",
    label: "Upload to a Drive folder",
    hint: "A folder link you paste",
    id: "drive",
    instruction: "Upload the report and the data files to the Drive folder below, in a new folder named after today's date.",
    settings: [["Drive folder", ""]],
    secrets: [],
  },
  {
    key: "database",
    label: "Write into a database",
    hint: "PostgreSQL, MySQL or a SQLite file",
    id: "database",
    instruction:
      "Write the results table into the table named below: one row per result. Add new rows, and update a row that is already there (the key column is below).",
    settings: [
      ["Engine", "PostgreSQL"],
      ["Host", ""],
      ["Port", "5432"],
      ["Database", ""],
      ["User", ""],
      ["Table", ""],
      ["Key column", ""],
    ],
    secrets: ["password"],
  },
  {
    key: "review",
    label: "Hand to an agent to report back",
    hint: "A colleague reviews it and tells you",
    id: "review",
    instruction: "Give the report to [a colleague's name], and ask them to review it and report back to me in chat with what matters in it.",
    settings: [],
    secrets: [],
  },
  {
    key: "other",
    label: "Anything else",
    hint: "Say what should happen, in your words",
    id: "delivery",
    instruction: "",
    settings: [],
    secrets: [],
  },
];

/** `base`, or `base-2`, `base-3` … : one no other delivery has. */
export function uniqueId(base: string, taken: readonly string[]): string {
  const clean = base.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^[^a-z]+/, "").replace(/-+$/, "").slice(0, 36) || "delivery";
  if (!taken.includes(clean)) return clean;
  for (let n = 2; ; n++) {
    const id = `${clean}-${n}`;
    if (!taken.includes(id)) return id;
  }
}

export function draftFromStarter(s: Starter, taken: readonly string[]): DeliveryDraft {
  return {
    id: uniqueId(s.id, taken),
    isNew: true,
    instruction: s.instruction,
    settings: s.settings.map(([n, v]) => settingRow(n, v)),
    secrets: s.secrets.map((n) => secretRow(n)),
    who: "",
    when: "success",
    minutes: DEFAULT_MINUTES,
    draftedBy: "",
  };
}

export function draftFromView(d: DeliveryView): DeliveryDraft {
  return {
    id: d.id,
    isNew: false,
    instruction: d.instruction,
    settings: (d.settings ?? []).map((s) => settingRow(s.name, s.value)),
    secrets: (d.secrets ?? []).map((s) => ({ ...secretRow(s.name), agentMaySee: s.agent_may_see, hasValue: s.has_value })),
    who: d.who,
    when: d.when,
    minutes: d.minutes,
    draftedBy: d.drafted_by,
  };
}

/** A stored delivery as the save sends it back unchanged (its approval, or its wait, stands). */
export function viewToInput(d: DeliveryView): DeliveryIn {
  return {
    id: d.id,
    instruction: d.instruction,
    settings: (d.settings ?? []).map((s) => ({ name: s.name, value: s.value })),
    secrets: (d.secrets ?? []).map((s) => ({ name: s.name, agent_may_see: s.agent_may_see })),
    who: d.who,
    when: d.when,
    minutes: d.minutes,
  };
}

export function draftToInput(d: DeliveryDraft): DeliveryIn {
  return {
    id: d.id,
    instruction: d.instruction.trim(),
    settings: d.settings.filter((s) => s.name.trim() || s.value.trim()).map((s) => ({ name: s.name.trim(), value: s.value.trim() })),
    secrets: d.secrets.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), agent_may_see: s.agentMaySee })),
    who: d.who,
    when: d.when,
    minutes: d.minutes,
  };
}

/** The whole list for the save: the stored ones as they are, `draft` in its place (or added at the end), `drop` left out. */
export function listForSave(stored: readonly DeliveryView[], draft: DeliveryDraft | null, drop: string | null = null): DeliveryIn[] {
  const out = stored.filter((d) => d.id !== drop).map((d) => (draft && d.id === draft.id ? draftToInput(draft) : viewToInput(d)));
  if (draft && draft.isNew && !stored.some((d) => d.id === draft.id)) out.push(draftToInput(draft));
  return out;
}

/** The secret values to write after the save: typed ones, and the ones asked to go (""). */
export function secretWrites(d: DeliveryDraft): { name: string; value: string }[] {
  return d.secrets
    .filter((s) => s.name.trim() && (s.value !== "" || (s.clear && s.hasValue)))
    .map((s) => ({ name: s.name.trim(), value: s.value !== "" ? s.value : "" }));
}

// ─── checks (the server makes them again) ────────────────────────────────

const dupes = (names: string[]): string[] => {
  const seen = new Set<string>();
  const out = new Set<string>();
  for (const n of names) {
    const k = n.trim().toLowerCase();
    if (seen.has(k)) out.add(n.trim());
    seen.add(k);
  }
  return [...out];
};

/** What stops the save, in words. */
export function draftProblems(d: DeliveryDraft, taken: readonly string[] = []): string[] {
  const out: string[] = [];
  if (!d.instruction.trim()) out.push("Say what should happen to the results.");
  if (d.instruction.length > MAX_INSTRUCTION) out.push(`The instruction is ${d.instruction.length.toLocaleString()} characters; at most ${MAX_INSTRUCTION.toLocaleString()}.`);
  if (!DELIVERY_ID.test(d.id) || (d.isNew && taken.includes(d.id))) out.push("This delivery needs another name inside Corvus (reload the page and add it again).");
  const settings = d.settings.filter((s) => s.name.trim() || s.value.trim());
  if (settings.length > MAX_SETTINGS) out.push(`At most ${MAX_SETTINGS} settings.`);
  for (const s of settings) {
    if (!s.name.trim()) out.push(`A setting with the value “${s.value.trim().slice(0, 40)}” needs a name.`);
    else if (!SETTING_NAME.test(s.name.trim())) out.push(`The setting name “${s.name.trim()}” must start with a letter and use letters, numbers, spaces and _ . ( ) / - only (at most 60).`);
    if (s.value.length > MAX_SETTING_VALUE) out.push(`The setting ${s.name.trim() || "without a name"} is longer than ${MAX_SETTING_VALUE.toLocaleString()} characters.`);
  }
  for (const n of dupes(settings.map((s) => s.name).filter((n) => n.trim()))) out.push(`Two settings are called ${n}.`);
  const secrets = d.secrets.filter((s) => s.name.trim() || s.value);
  if (secrets.length > MAX_SECRETS) out.push(`At most ${MAX_SECRETS} secrets.`);
  for (const s of secrets) {
    if (!s.name.trim()) out.push("A secret needs a name.");
    else if (!SECRET_NAME.test(s.name.trim())) out.push(`The secret name “${s.name.trim()}” must start with a letter and use letters, numbers, _ and - only (at most 40, no spaces).`);
    if (s.value.length > MAX_SECRET_VALUE) out.push(`The value of ${s.name.trim() || "a secret"} is longer than ${MAX_SECRET_VALUE.toLocaleString()} characters.`);
  }
  for (const n of dupes(secrets.map((s) => s.name).filter((n) => n.trim()))) out.push(`Two secrets are called ${n}.`);
  if (!Number.isInteger(d.minutes) || d.minutes < MIN_MINUTES || d.minutes > MAX_MINUTES) out.push(`The time limit is between ${MIN_MINUTES} and ${MAX_MINUTES} minutes.`);
  return out;
}

/** Worth knowing before saving; never stops it. */
export function draftWarnings(d: DeliveryDraft): string[] {
  const out: string[] = [];
  const empty = d.settings.filter((s) => s.name.trim() && !s.value.trim()).map((s) => s.name.trim());
  if (empty.length) out.push(`Still empty: ${empty.join(", ")}.`);
  for (const s of d.secrets) if (s.name.trim() && !s.hasValue && !s.value) out.push(`The secret ${s.name.trim()} has no value yet: it cannot be used until you give it one.`);
  if (/\[[^\]]*\]/.test(d.instruction)) out.push("The instruction still has a [ … ] to fill in.");
  return out;
}

/** The run inputs the text uses ({{params.x}}) that the workflow does not ask for. */
export function unknownParams(text: string, params: readonly string[]): string[] {
  const used = [...text.matchAll(/\{\{\s*params\.([A-Za-z_][A-Za-z0-9_-]{0,63})\s*\}\}/g)].map((m) => m[1]);
  return [...new Set(used.filter((n) => !params.includes(n)))];
}

// ─── words ───────────────────────────────────────────────────────────────

export const WHEN_WORDS: Record<DeliveryWhen, string> = {
  success: "Only when the run completes",
  always: "Whenever the run ends",
};

export function whenShort(when: DeliveryWhen): string {
  return when === "always" ? "whenever the run ends" : "when the run completes";
}

export interface StateWord {
  label: string;
  tone: StatusTone;
}

export const STATE_WORDS: Record<DeliveryState, StateWord> = {
  waiting: { label: "Waits for the run to end", tone: "neutral" },
  needs_approval: { label: "Not approved", tone: "warning" },
  due: { label: "About to be sent", tone: "live" },
  sending: { label: "Being sent", tone: "live" },
  delivered: { label: "Delivered", tone: "success" },
  partial: { label: "Partly delivered", tone: "warning" },
  failed: { label: "Failed", tone: "danger" },
  blocked: { label: "Blocked", tone: "danger" },
  skipped: { label: "Skipped", tone: "neutral" },
};

export function stateWord(state: string): StateWord {
  return STATE_WORDS[state as DeliveryState] ?? { label: state ? state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, " ") : "Unknown", tone: "neutral" };
}

/**
 * The editor card's closed lines and the count waiting for approval: one
 * delivery = its first line, then who carries it out and when ("Julia ·
 * when the run completes"); several = how many, then their first lines.
 */
export function cardLine(list: readonly Pick<DeliveryView, "first_line" | "approved" | "who_name" | "when">[] | undefined, saved: boolean): { title: string; summary: string; waiting: number } {
  if (!saved) return { title: "Nothing is sent yet: save the workflow first", summary: "", waiting: 0 };
  if (!list || list.length === 0) return { title: "Nothing is sent when a run ends", summary: "", waiting: 0 };
  const waiting = list.filter((d) => !d.approved).length;
  const lines = list.map((d) => (d.first_line || "A delivery").replace(/[.:;,]\s*$/, ""));
  if (list.length === 1) return { title: lines[0], summary: `${list[0].who_name || "The usual agent"} · ${whenShort(list[0].when ?? "success")}`, waiting };
  return { title: `${list.length} deliveries at the end of each run`, summary: lines.join(" · "), waiting };
}

/** A link from an agent's report is shown as a link only when it is a web address; anything else is text. */
export function safeLink(url: string): string | null {
  const u = url.trim();
  return /^https?:\/\/[^\s<>"']+$/i.test(u) ? u : null;
}

/** A link's text: the host and the start of its path, never a whole query string. */
export function linkLabel(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === "/" ? "" : u.pathname;
    const shown = `${u.host}${path}`;
    return shown.length > 60 ? `${shown.slice(0, 57)}…` : shown;
  } catch {
    return url.length > 60 ? `${url.slice(0, 57)}…` : url;
  }
}

/** "12 rows" for a database delivery's count. */
export function rowsLine(rows: number | null | undefined): string {
  if (rows == null) return "";
  return `${rows.toLocaleString()} ${rows === 1 ? "row" : "rows"} written`;
}
